<script setup>
import { ref, computed, useId } from 'vue';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  ticket: { type: Object, required: true },
  workflowFields: { type: Array, default: () => [] },
  editable: { type: Boolean, default: true },
});

const emit = defineEmits(['updated']);

/** gridSpan full = full row; half = half width from sm breakpoint (denser than a single column). */
const ADDRESS_DISPLAY = [
  {
    key: 'address_street',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_STREET',
    gridSpan: 'full',
  },
  {
    key: 'address_exterior_number',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_EXTERIOR_NUMBER',
    gridSpan: 'half',
  },
  {
    key: 'address_interior_number',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_INTERIOR_NUMBER',
    gridSpan: 'half',
  },
  {
    key: 'address_colony',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_COLONY',
    gridSpan: 'half',
  },
  {
    key: 'address_postal_code',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_POSTAL_CODE',
    gridSpan: 'half',
  },
  {
    key: 'address_city',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_CITY',
    gridSpan: 'half',
  },
  {
    key: 'address_state',
    labelI18n: 'TICKETS.SIDEBAR.ADDRESS_STATE',
    gridSpan: 'half',
  },
];

const hasAnyAddress = computed(() =>
  ADDRESS_DISPLAY.some(f => props.ticket?.[f.key])
);

const addressEntries = computed(() =>
  ADDRESS_DISPLAY.filter(f => props.ticket?.[f.key])
);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const showAddDropdown = ref(false);
const isSaving = ref({});
/** Collapsed by default to save vertical space in ticket sidebar. */
const addressSectionOpen = ref(false);
const customAttrsSectionOpen = ref(false);
const addressPanelId = useId();
const customAttrsPanelId = useId();

const expandCollapseLabel = (titleKey, open) =>
  `${t(titleKey)} — ${open ? t('TICKETS.CUSTOM_ATTRIBUTES.COLLAPSE') : t('TICKETS.CUSTOM_ATTRIBUTES.EXPAND')}`;

const ADDRESS_SUBFIELD_KEYS = [
  'street',
  'colony',
  'postal_code',
  'city',
  'state',
];

const addressCustomAttributeKey = (field, subField) =>
  `${String(field?.key || 'address').trim()}_${subField}`;

const getAddressSubFields = field => {
  const raw = field?.sub_fields ?? field?.subFields;
  if (!Array.isArray(raw) || raw.length === 0) return ADDRESS_SUBFIELD_KEYS;

  const normalized = raw
    .map(item => (typeof item === 'object' ? item.key : item))
    .map(item =>
      String(item || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/-/g, '_')
        .toLowerCase()
    )
    .filter(item => ADDRESS_SUBFIELD_KEYS.includes(item));

  return [...new Set(normalized)].length
    ? [...new Set(normalized)]
    : ADDRESS_SUBFIELD_KEYS;
};

// All ticket attribute definitions from store
const attributeDefinitions = computed(
  () => getters['attributes/getTicketAttributes'].value || []
);

// Current custom attribute values from ticket
const customAttributes = computed(() => props.ticket?.custom_attributes || {});

// Workflow field keys (required/optional from subcategory)
const workflowKeys = computed(() =>
  props.workflowFields.flatMap(field => {
    if (field.type !== 'address') return [field.key];
    return getAddressSubFields(field).map(subField =>
      addressCustomAttributeKey(field, subField)
    );
  })
);

// Build display list: workflow fields first, then other populated attributes
const displayAttributes = computed(() => {
  // 1. Workflow fields (ordered)
  const sortedWorkflow = [...props.workflowFields].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const workflowAttrs = sortedWorkflow.reduce((acc, wf) => {
    const keys =
      wf.type === 'address'
        ? getAddressSubFields(wf).map(subField =>
            addressCustomAttributeKey(wf, subField)
          )
        : [wf.key];

    keys.forEach(key => {
      const def = attributeDefinitions.value.find(d => d.attributeKey === key);
      if (!def) return;

      acc.push({
        ...def,
        value: customAttributes.value[key] ?? '',
        isWorkflow: true,
        priority: wf.priority || 'optional',
      });
    });

    return acc;
  }, []);

  // 2. Other attributes that have values but aren't workflow fields
  const otherAttrs = Object.entries(customAttributes.value)
    .filter(
      ([key]) => !key.startsWith('_') && !workflowKeys.value.includes(key)
    )
    .reduce((acc, [key, value]) => {
      const def = attributeDefinitions.value.find(d => d.attributeKey === key);
      if (def) {
        acc.push({
          ...def,
          value,
          isWorkflow: false,
          priority: 'optional',
        });
      }
      return acc;
    }, []);

  return [...workflowAttrs, ...otherAttrs];
});

// Unused attributes (can be added)
const unusedAttributes = computed(() => {
  const usedKeys = displayAttributes.value.map(a => a.attributeKey);
  return attributeDefinitions.value.filter(
    d => !usedKeys.includes(d.attributeKey)
  );
});

const updateAttribute = async (key, value) => {
  isSaving.value = { ...isSaving.value, [key]: true };
  try {
    const updatedAttrs = { ...customAttributes.value, [key]: value };
    await store.dispatch('tickets/updateTicket', {
      id: props.ticket.display_id,
      custom_attributes: updatedAttrs,
    });
    emit('updated');
    useAlert(t('TICKETS.CUSTOM_ATTRIBUTES.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('TICKETS.CUSTOM_ATTRIBUTES.UPDATE_ERROR'));
  } finally {
    isSaving.value = { ...isSaving.value, [key]: false };
  }
};

const addAttribute = key => {
  showAddDropdown.value = false;
  updateAttribute(key, '');
};

const deleteAttribute = async key => {
  const updatedAttrs = { ...customAttributes.value };
  delete updatedAttrs[key];
  try {
    await store.dispatch('tickets/updateTicket', {
      id: props.ticket.display_id,
      custom_attributes: updatedAttrs,
    });
    emit('updated');
  } catch {
    useAlert(t('TICKETS.CUSTOM_ATTRIBUTES.UPDATE_ERROR'));
  }
};

// Fetch definitions on mount
store.dispatch('attributes/get');
</script>

<template>
  <section class="flex flex-col gap-6">
    <!-- Address -->
    <div
      v-if="hasAnyAddress"
      class="flex min-w-0 w-full flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex w-full items-center justify-between gap-2 border-b border-n-weak px-3 py-2.5 text-left hover:bg-n-alpha-1 sm:px-4"
        :aria-expanded="addressSectionOpen"
        :aria-controls="addressPanelId"
        :aria-label="
          expandCollapseLabel('TICKETS.SIDEBAR.ADDRESS', addressSectionOpen)
        "
        @click="addressSectionOpen = !addressSectionOpen"
      >
        <div class="flex min-w-0 flex-1 items-center gap-1.5">
          <span
            class="i-lucide-map-pin size-4 shrink-0 text-n-slate-10"
            aria-hidden="true"
          />
          <span
            class="truncate text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ $t('TICKETS.SIDEBAR.ADDRESS') }}
          </span>
        </div>
        <span
          class="i-lucide-chevron-down size-5 shrink-0 text-n-slate-11 transition-transform duration-200"
          :class="{ 'rotate-180': addressSectionOpen }"
          aria-hidden="true"
        />
      </button>
      <div
        v-show="addressSectionOpen"
        :id="addressPanelId"
        class="flex flex-col gap-3 px-3 pb-3 pt-2 sm:px-4 sm:pb-4"
        role="region"
      >
        <dl class="m-0 grid min-w-0 grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
          <div
            v-for="f in addressEntries"
            :key="f.key"
            class="flex min-w-0 flex-col gap-1"
            :class="f.gridSpan === 'full' ? 'sm:col-span-2' : ''"
          >
            <dt class="text-xs font-medium leading-none text-n-slate-11">
              {{ $t(f.labelI18n) }}
            </dt>
            <dd
              class="m-0 text-sm font-medium leading-snug text-n-slate-12 break-words"
            >
              {{ props.ticket?.[f.key] }}
            </dd>
          </div>
        </dl>
      </div>
    </div>

    <!-- Custom / personal attributes (collapsible; add control stays in header) -->
    <div
      class="flex min-w-0 w-full flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
    >
      <div
        class="flex w-full items-stretch gap-1 border-b border-n-weak sm:gap-2"
      >
        <button
          type="button"
          class="flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-n-alpha-1 sm:px-4"
          :aria-expanded="customAttrsSectionOpen"
          :aria-controls="customAttrsPanelId"
          :aria-label="
            expandCollapseLabel(
              'TICKETS.CUSTOM_ATTRIBUTES.TITLE',
              customAttrsSectionOpen
            )
          "
          @click="customAttrsSectionOpen = !customAttrsSectionOpen"
        >
          <span class="flex min-w-0 items-center gap-1.5">
            <span
              class="i-lucide-clipboard-list size-4 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <span
              class="truncate text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{ $t('TICKETS.CUSTOM_ATTRIBUTES.TITLE') }}
            </span>
          </span>
          <span
            class="i-lucide-chevron-down size-5 shrink-0 text-n-slate-11 transition-transform duration-200"
            :class="{ 'rotate-180': customAttrsSectionOpen }"
            aria-hidden="true"
          />
        </button>
        <div
          v-if="editable && unusedAttributes.length > 0"
          class="relative flex shrink-0 items-center pr-2 sm:pr-3"
        >
          <Button
            icon="i-lucide-plus"
            slate
            xs
            faded
            @click.stop="showAddDropdown = !showAddDropdown"
          />
          <div
            v-if="showAddDropdown"
            class="absolute right-0 top-9 z-20 max-h-48 w-56 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
          >
            <button
              v-for="attr in unusedAttributes"
              :key="attr.attributeKey"
              class="w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="addAttribute(attr.attributeKey)"
            >
              {{ attr.attributeDisplayName }}
            </button>
          </div>
        </div>
      </div>
      <div
        v-show="customAttrsSectionOpen"
        :id="customAttrsPanelId"
        class="flex flex-col gap-3 px-3 pb-3 pt-2 sm:px-4 sm:pb-4"
        role="region"
      >
        <div
          v-if="displayAttributes.length === 0"
          class="text-sm text-n-slate-10"
        >
          {{ $t('TICKETS.CUSTOM_ATTRIBUTES.EMPTY') }}
        </div>

        <div
          v-for="attr in displayAttributes"
          :key="attr.attributeKey"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ attr.attributeDisplayName }}
            </label>
            <span
              v-if="attr.priority === 'required'"
              class="text-xs text-n-ruby-9"
            >
              *
            </span>
          </div>

          <!-- Checkbox type -->
          <label
            v-if="attr.attributeDisplayType === 'checkbox'"
            class="flex items-center gap-2"
          >
            <input
              type="checkbox"
              :checked="attr.value === true || attr.value === 'true'"
              :disabled="!editable"
              class="rounded"
              @change="
                updateAttribute(attr.attributeKey, $event.target.checked)
              "
            />
          </label>

          <!-- List type -->
          <select
            v-else-if="attr.attributeDisplayType === 'list'"
            :value="attr.value"
            :disabled="!editable"
            class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm focus:border-woot-500 focus:outline-none"
            @change="updateAttribute(attr.attributeKey, $event.target.value)"
          >
            <option value="">--</option>
            <option
              v-for="opt in attr.attributeValues || []"
              :key="opt"
              :value="opt"
            >
              {{ opt }}
            </option>
          </select>

          <!-- Date type -->
          <input
            v-else-if="attr.attributeDisplayType === 'date'"
            type="date"
            :value="attr.value ? attr.value.split('T')[0] : ''"
            :disabled="!editable"
            class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm focus:border-woot-500 focus:outline-none"
            @change="updateAttribute(attr.attributeKey, $event.target.value)"
          />

          <!-- Number type -->
          <input
            v-else-if="attr.attributeDisplayType === 'number'"
            type="number"
            :value="attr.value"
            :disabled="!editable"
            class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm focus:border-woot-500 focus:outline-none"
            @change="
              updateAttribute(attr.attributeKey, Number($event.target.value))
            "
          />

          <!-- Link type -->
          <input
            v-else-if="attr.attributeDisplayType === 'link'"
            type="url"
            :value="attr.value"
            :disabled="!editable"
            class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm focus:border-woot-500 focus:outline-none"
            @change="updateAttribute(attr.attributeKey, $event.target.value)"
          />

          <!-- Default: text type -->
          <input
            v-else
            type="text"
            :value="attr.value"
            :disabled="!editable"
            class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm focus:border-woot-500 focus:outline-none"
            @change="updateAttribute(attr.attributeKey, $event.target.value)"
          />

          <!-- Delete button for non-workflow attributes -->
          <button
            v-if="editable && !attr.isWorkflow"
            class="self-end text-xs text-n-slate-10 hover:text-n-ruby-9"
            @click="deleteAttribute(attr.attributeKey)"
          >
            {{ $t('TICKETS.CUSTOM_ATTRIBUTES.REMOVE') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
